import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useSelector } from "react-redux";
import { Wallet, CheckCircle2, User, Headphones, Briefcase, RefreshCw, ShieldCheck, CalendarClock, Pencil } from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import PaymentPlanModal from "../../components/dashboard/PaymentPlanModal";
import PreApprovalModal from "../../components/dashboard/PreApprovalModal";
import { FilterDismissLink } from "../../components/dashboard/BackNavLink";
import FilteredDataTable from "../../components/dashboard/FilteredDataTable";
import StatusBadge from "../../components/dashboard/StatusBadge";
import StatCard from "../../components/dashboard/StatCard";
import StatCardGrid from "../../components/dashboard/StatCardGrid";
import { PAYMENTS_FILTER_CONFIG, PLANS_FILTER_CONFIG } from "../../config/tableFilterConfigs";
import { MOCK_PAYMENTS, formatCurrency, formatPercent } from "../../data/mock/dashboardData";
import { USE_MOCK_AUTH } from "../../config/api";
import { fetchOpsPayments, fetchOpsPaymentsSummary } from "../../services/dashboardApi";
import {
  checkPreApprovalStatus,
  fetchOpsPaymentPlans,
  fetchOpsPreApprovals,
  verifyOpsPaymentStatus,
} from "../../services/paymentApi";
import useRole from "../../hooks/useRole";
import { showError, showSuccess } from "../../utils/toast";
import { opsCasesPath } from "../../utils/opsCasesPath";
import { isPlaceholderName, personNameColumns } from "../../utils/personName";

function recordCase(row) {
  return {
    id: row.case_id || row.caseId,
    customer: row.customer_name || row.customer,
    accountNumber: row.account_number || row.accountNumber,
    phone: row.msisdn,
    outstandingAmount: row.amountOwed,
  };
}

export default function PaymentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawCustomer = searchParams.get("customer");
  const customerParam = rawCustomer && !isPlaceholderName(rawCustomer) ? rawCustomer : null;
  const caseParam = searchParams.get("caseId");
  const accountParam = searchParams.get("account") || searchParams.get("account_number");
  const tabParam = searchParams.get("tab");
  const token = useSelector((state) => state.auth.token);
  const { isSuperAdmin, isExecutiveManager, isCallCenterManager, isLegalOfficer } = useRole();
  const isOrgWide = isSuperAdmin || isExecutiveManager;
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const [tab, setTab] = useState(() =>
    ["ledger", "mandates", "plans"].includes(tabParam) ? tabParam : "ledger"
  );

  useEffect(() => {
    if (["ledger", "mandates", "plans"].includes(tabParam) && tabParam !== tab) {
      setTab(tabParam);
    }
  }, [tabParam, tab]);
  const [rows, setRows] = useState(() => (useLiveApi ? [] : MOCK_PAYMENTS));
  const [mandates, setMandates] = useState([]);
  const [plans, setPlans] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(useLiveApi);
  const [updatingId, setUpdatingId] = useState(null);
  const [editingMandate, setEditingMandate] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [historyMsisdn, setHistoryMsisdn] = useState("");

  useEffect(() => {
    if (!useLiveApi) {
      setRows(MOCK_PAYMENTS);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      fetchOpsPayments(token),
      fetchOpsPaymentsSummary(token),
      fetchOpsPreApprovals(token),
      fetchOpsPaymentPlans(token),
    ])
      .then(([paymentsResult, summaryResult, mandateResult, planResult]) => {
        if (cancelled) return;
        if (paymentsResult.status === "fulfilled") {
          setRows(paymentsResult.value || []);
        } else {
          showError(paymentsResult.reason?.message || "Could not load payments.");
        }
        if (summaryResult.status === "fulfilled") {
          setSummary(summaryResult.value || null);
        }
        if (mandateResult.status === "fulfilled") setMandates(mandateResult.value || []);
        if (planResult.status === "fulfilled") setPlans(planResult.value || []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, useLiveApi]);

  const refreshLive = useCallback(() => {
    if (!useLiveApi || !token) return Promise.resolve();
    return Promise.allSettled([
      fetchOpsPayments(token, {}, { force: true }),
      fetchOpsPaymentsSummary(token, { force: true }),
      fetchOpsPreApprovals(token, {}, { force: true }),
      fetchOpsPaymentPlans(token, {}, { force: true }),
    ]).then(([paymentsResult, summaryResult, mandateResult, planResult]) => {
      if (paymentsResult.status === "fulfilled") setRows(paymentsResult.value || []);
      if (summaryResult.status === "fulfilled") setSummary(summaryResult.value || null);
      if (mandateResult.status === "fulfilled") setMandates(mandateResult.value || []);
      if (planResult.status === "fulfilled") setPlans(planResult.value || []);
    });
  }, [token, useLiveApi]);

  const handleVerifyStatus = useCallback(
    async (row) => {
      const paymentId = row.payment_id || row.id;
      if (!paymentId || !useLiveApi) return;
      setUpdatingId(paymentId);
      try {
        await verifyOpsPaymentStatus(token, paymentId);
        showSuccess("Payment status refreshed from SevoTransact.");
        await refreshLive();
      } catch (error) {
        showError(error.message || "Could not verify payment status.");
      } finally {
        setUpdatingId(null);
      }
    },
    [refreshLive, token, useLiveApi]
  );

  const handleMandateStatus = useCallback(
    async (row) => {
      if (!row.reference_id || !useLiveApi) return;
      setUpdatingId(row.reference_id);
      try {
        const payload = await checkPreApprovalStatus(token, row.reference_id);
        const mandate = payload?.mandate || payload;
        showSuccess(
          mandate?.status
            ? `Mandate ${row.reference_id} is ${mandate.approval_status || mandate.status}.`
            : "Mandate status refreshed."
        );
        await refreshLive();
      } catch (error) {
        showError(error.message || "Could not check mandate status.");
      } finally {
        setUpdatingId(null);
      }
    },
    [refreshLive, token, useLiveApi]
  );

  const accountNeedle = String(accountParam || "").trim().toLowerCase();

  const visible = useMemo(() => {
    let data = rows;
    if (accountNeedle) {
      data = data.filter(
        (row) => String(row.accountNumber || row.account_number || "").trim().toLowerCase() === accountNeedle
      );
    } else if (customerParam) {
      data = data.filter((p) => p.customer === customerParam);
    }
    if (caseParam && !accountNeedle) data = data.filter((p) => p.caseId === caseParam);
    return data;
  }, [rows, customerParam, caseParam, accountNeedle]);

  const initialFilters = useMemo(() => {
    if (customerParam) return { customer: [customerParam] };
    if (caseParam) return { caseId: [caseParam] };
    return undefined;
  }, [customerParam, caseParam]);

  const totalCollected = useMemo(
    () =>
      visible.reduce(
        (sum, payment) => sum + (payment.status === "completed" ? Number(payment.amount) || 0 : 0),
        0
      ),
    [visible]
  );

  const reconciliationRate = Number(
    summary?.collected_reconciliation_rate ??
      (visible.length
        ? (visible.filter((row) => row.reconciled).length / visible.length) * 100
        : 0)
  );

  const paymentColumns = [
    { key: "numericId", header: "ID", type: "id" },
    { key: "receivedAt", header: "Date & time", type: "datetime" },
    ...personNameColumns(),
    { key: "caseId", header: "Case ID", type: "id" },
    { key: "amount", header: "Payment amount", type: "currency", className: "whitespace-nowrap" },
    { key: "amountOwed", header: "Amount owed", type: "currency" },
    { key: "amountPaid", header: "Amount paid", type: "currency" },
    { key: "outstandingAmount", header: "Outstanding", type: "currency" },
    { key: "paymentType", header: "Payment type", type: "channel" },
    { key: "network", header: "Network", type: "network" },
    { key: "method", header: "Interaction method", type: "channel" },
    {
      key: "status",
      header: "Status",
      type: "badge",
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: "comment",
      header: "Comment",
      render: (row) => {
        const tags = row.commentPresets?.length ? row.commentPresets.join(" · ") : "";
        const text = row.comment || "";
        if (!tags && !text) return <span className="text-primary/35">—</span>;
        return (
          <div className="max-w-[220px]">
            {tags ? <p className="text-xs font-semibold text-primary/70">{tags}</p> : null}
            {text ? <p className="truncate text-xs text-primary/50">{text}</p> : null}
          </div>
        );
      },
    },
    { key: "reference", header: "Reference", type: "id" },
    {
      key: "actions",
      header: "Actions",
      align: "center",
      headerClassName: "text-center",
      actions: (row) =>
        [
          {
            key: "case",
            label: "View case",
            description: "Open collection case",
            icon: Briefcase,
            tone: "primary",
            to: opsCasesPath({
              account: row.accountNumber || row.account_number,
              caseId: row.caseId || row.case_id,
            }),
          },
          {
            key: "contact",
            label: "Contact history",
            icon: Headphones,
            tone: "sky",
            to: `/dashboard/contact-centre?customer=${encodeURIComponent(row.customer)}&tab=calls`,
          },
          {
            key: "verify",
            label: updatingId === (row.payment_id || row.id) ? "Checking…" : "Verify status",
            icon: RefreshCw,
            tone: "accent",
            onClick: () => handleVerifyStatus(row),
            hidden: isExecutiveManager || !useLiveApi || !row.referenceId,
          },
        ].filter((action) => !action.hidden),
    },
  ];

  const mandateColumns = [
    { key: "reference_id", header: "Reference", type: "id" },
    ...personNameColumns(),
    { key: "account_number", header: "Account", type: "id" },
    { key: "case_id", header: "Case", type: "id" },
    { key: "msisdn", header: "Wallet", type: "id" },
    { key: "network", header: "Network", type: "network" },
    { key: "created_at", header: "Sent", type: "datetime" },
    { key: "end_date", header: "End", type: "datetime" },
    { key: "description", header: "Description" },
    {
      key: "approval_status",
      header: "Approval",
      type: "badge",
      render: (row) => <StatusBadge status={row.approval_status || row.status} />,
    },
    { key: "initiated_by", header: "Requested by", type: "name" },
    {
      key: "actions",
      header: "Actions",
      align: "center",
      actions: (row) =>
        [
          {
            key: "case",
            label: "View case",
            icon: Briefcase,
            tone: "primary",
            to: opsCasesPath({
              account: row.accountNumber || row.account_number,
              caseId: row.case_id,
            }),
          },
          {
            key: "edit",
            label: "Edit mandate",
            description: "Update the wallet, end date, and description",
            icon: Pencil,
            tone: "primary",
            onClick: () => setEditingMandate(row),
          },
          {
            key: "status",
            label: updatingId === row.reference_id ? "Checking…" : "Check status",
            icon: RefreshCw,
            tone: "accent",
            onClick: () => handleMandateStatus(row),
          },
        ].filter((action) => !action.hidden),
    },
  ];

  const planColumns = [
    { key: "plan_id", header: "Plan", type: "id" },
    ...personNameColumns(),
    { key: "account_number", header: "Account", type: "id" },
    { key: "case_id", header: "Case", type: "id" },
    { key: "debit_amount", header: "Debit", type: "currency" },
    { key: "plan_total", header: "Plan total", type: "currency" },
    { key: "instalment_count", header: "Instalments" },
    { key: "frequency", header: "Frequency", type: "channel" },
    { key: "payment_method", header: "Method" },
    { key: "plan_start_date", header: "Start", type: "date" },
    { key: "plan_end_date", header: "End", type: "date" },
    { key: "first_due_date", header: "First due", type: "date" },
    { key: "status", header: "Status", type: "badge", render: (row) => <StatusBadge status={row.status} /> },
    { key: "created_by", header: "Created by", type: "name" },
    {
      key: "actions",
      header: "Actions",
      align: "center",
      actions: (row) =>
        [
          {
            key: "case",
            label: "View case",
            icon: Briefcase,
            tone: "primary",
            to: opsCasesPath({
              account: row.accountNumber || row.account_number,
              caseId: row.case_id || row.caseId,
            }),
          },
          {
            key: "edit",
            label: "Edit plan",
            description: "Update the schedule and save it",
            icon: Pencil,
            tone: "accent",
            onClick: () => setEditingPlan(row),
          },
        ].filter((action) => !action.hidden),
    },
  ];

  const visibleMandates = useMemo(() => {
    let data = mandates;
    if (accountNeedle) {
      data = data.filter(
        (row) => String(row.accountNumber || row.account_number || "").trim().toLowerCase() === accountNeedle
      );
    } else if (customerParam) {
      data = data.filter((row) => row.customer_name === customerParam);
    }
    if (caseParam && !accountNeedle) data = data.filter((row) => row.case_id === caseParam);
    if (historyMsisdn.trim()) {
      const digits = historyMsisdn.replace(/\D+/g, "");
      data = data.filter((row) => String(row.msisdn || "").includes(digits));
    }
    return data;
  }, [mandates, customerParam, caseParam, accountNeedle, historyMsisdn]);

  const visiblePlans = useMemo(() => {
    let data = plans;
    if (accountNeedle) {
      data = data.filter(
        (row) => String(row.accountNumber || row.account_number || "").trim().toLowerCase() === accountNeedle
      );
    } else if (customerParam) {
      data = data.filter((row) => (row.customer_name || row.customer) === customerParam);
    }
    if (caseParam && !accountNeedle) data = data.filter((row) => (row.case_id || row.caseId) === caseParam);
    return data;
  }, [plans, customerParam, caseParam, accountNeedle]);

  const setTabAndUrl = (next) => {
    setTab(next);
    const nextParams = new URLSearchParams(searchParams);
    if (next === "ledger") nextParams.delete("tab");
    else nextParams.set("tab", next);
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <PageShell
      title="Payments"
      description={
        isOrgWide
          ? "Full, partial, scheduled and instalment payments collected across institutions."
          : isCallCenterManager
            ? "Loan amount and payments collected on cases for your assigned institutions."
            : isLegalOfficer
              ? "Loan amount and payments on escalations assigned to you."
              : "Loan amount and payments collected on cases assigned to you."
      }
    >
      {(customerParam || caseParam || accountParam) && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/10 bg-gradient-to-r from-primary/[0.04] to-accent/[0.06] px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-primary shadow-sm">
              <User className="h-4 w-4" />
            </span>
            <div>
              <p className="text-xs font-semibold text-primary">
                {accountParam ? "Payments for account" : caseParam ? "Payments for case" : "Payments for"}
              </p>
              <p className="text-sm font-bold text-primary">
                {accountParam ?? caseParam ?? customerParam}
              </p>
            </div>
          </div>
          <FilterDismissLink fallback="/dashboard/payments" fallbackLabel="Show all payments" />
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        {[
          { id: "ledger", label: "Ledger", icon: Wallet },
          { id: "mandates", label: "Mandates", icon: ShieldCheck },
          { id: "plans", label: "Payment plans", icon: CalendarClock },
        ].map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTabAndUrl(item.id)}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold ${
              tab === item.id ? "bg-primary text-white" : "bg-white text-primary/50 ring-1 ring-primary/10"
            }`}
          >
            <item.icon className="h-3.5 w-3.5" />
            {item.label}
          </button>
        ))}
      </div>

      <StatCardGrid cols={3}>
        {isOrgWide ? (
          <StatCard
            label="Collected"
            value={loading ? "…" : formatCurrency(summary?.total_collected ?? totalCollected)}
            icon={Wallet}
            tone="primary"
          />
        ) : (
          <StatCard
            label="Loan amount"
            value={loading ? "…" : formatCurrency(summary?.loan_amount ?? 0)}
            icon={Briefcase}
            tone="primary"
          />
        )}
        {isOrgWide ? (
          <StatCard
            label="Reconciliation rate"
            value={loading ? "…" : formatPercent(reconciliationRate)}
            icon={CheckCircle2}
            tone="amber"
            progress={reconciliationRate}
          />
        ) : (
          <StatCard
            label="Total paid"
            value={loading ? "…" : formatCurrency(summary?.total_paid ?? summary?.total_collected ?? 0)}
            icon={Wallet}
            tone="accent"
          />
        )}
        <StatCard
          label="Payment records"
          value={loading ? "…" : summary?.payments ?? visible.length}
          icon={Wallet}
          tone="emerald"
        />
      </StatCardGrid>

      {tab === "ledger" ? (
        <FilteredDataTable
          key={`payments-${customerParam ?? caseParam ?? "all"}-${loading ? "loading" : "ready"}`}
          data={visible}
          columns={paymentColumns}
          config={{ ...PAYMENTS_FILTER_CONFIG, title: "Payment filters" }}
          initialFilters={initialFilters}
          pageSize={10}
          emptyMessage={loading ? "Loading payments…" : "No payments yet."}
        />
      ) : null}

      {tab === "mandates" ? (
        <div>
          <div className="mb-3 flex flex-wrap items-end gap-2">
            <label className="text-xs font-semibold text-primary/50">
              Filter by wallet
              <input
                value={historyMsisdn}
                onChange={(e) => setHistoryMsisdn(e.target.value)}
                placeholder="233…"
                className="mt-1 block w-56 rounded-lg border border-primary/10 px-3 py-2 text-sm text-primary"
              />
            </label>
          </div>
          <FilteredDataTable
            key={`mandates-${historyMsisdn}`}
            data={visibleMandates}
            columns={mandateColumns}
            config={{
              title: "Mandate filters",
              searchKeys: ["reference_id", "customer_name", "case_id", "msisdn", "account_number"],
              searchPlaceholder: "Search reference, customer, account, wallet…",
              fields: [
                { key: "status", label: "Status", type: "multi", optionsFrom: "status", section: "Status" },
                { key: "network", label: "Network", type: "multi", optionsFrom: "network", section: "Channel" },
              ],
            }}
            pageSize={10}
            emptyMessage={loading ? "Loading mandates…" : "No pre-approval mandates yet."}
          />
        </div>
      ) : null}

      {tab === "plans" ? (
        <FilteredDataTable
          data={visiblePlans}
          columns={planColumns}
          config={{ ...PLANS_FILTER_CONFIG, title: "Plan filters" }}
          pageSize={10}
          emptyMessage={loading ? "Loading plans…" : "No payment plans yet. Create one from a case."}
        />
      ) : null}

      <PreApprovalModal
        caseRow={editingMandate ? recordCase(editingMandate) : null}
        record={editingMandate}
        onClose={() => setEditingMandate(null)}
        onComplete={() => {
          setEditingMandate(null);
          refreshLive();
        }}
      />
      <PaymentPlanModal
        caseRow={editingPlan ? recordCase(editingPlan) : null}
        record={editingPlan}
        onClose={() => setEditingPlan(null)}
        onComplete={() => {
          setEditingPlan(null);
          refreshLive();
        }}
      />
    </PageShell>
  );
}
